import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {
    Home, Languages, Mic, Mail, MessageSquare, Users, Book, MessageCircle,
    Settings, CreditCard, LogOut, Bot, Scan, Type, FileSearch, ChevronDown,
    HandCoins, ShieldCheck, Megaphone, Database
} from 'lucide-react';

const Sidebar = () => {
    const location = useLocation();
    const currentPath = location.pathname;

    const scrollRef = useRef(null);
    const lastScrollTopRef = useRef(0);       // track scroll direction
    const [showScrollHint, setShowScrollHint] = useState(true);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        // Hide the hint entirely if the content doesn't overflow.
        const checkOverflow = () => {
            if (el.scrollHeight <= el.clientHeight) setShowScrollHint(false);
        };
        checkOverflow();

        const handleScroll = () => {
            const cur = el.scrollTop;
            const goingUp = cur < lastScrollTopRef.current;
            const atTop = cur < 50;

            // Show when scrolling up or near top; hide when scrolling down.
            if (goingUp || atTop) {
                setShowScrollHint(true);
            } else {
                setShowScrollHint(false);
            }
            lastScrollTopRef.current = cur;
        };

        el.addEventListener('scroll', handleScroll, {passive: true});
        window.addEventListener('resize', checkOverflow);

        // initialize state
        handleScroll();

        return () => {
            el.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', checkOverflow);
        };
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        window.location.href = '/';
    };

    // Smooth scroll + hide immediately on click
    const handleHintClick = () => {
        const el = scrollRef.current;
        if (!el) return;
        setShowScrollHint(false);                            // hide right away
        const step = Math.max(220, Math.floor(el.clientHeight * 0.6));
        el.scrollBy({top: step, behavior: 'smooth'});
    };

    const navItems = [
        {path: '/dashboard', icon: <Home size={20}/>, label: 'Dashboard'},
        {path: '/translation', icon: <Languages size={20}/>, label: 'Urdu Translation'},
        {path: '/speech-to-text', icon: <Mic size={20}/>, label: 'Urdu Speech to Text'},
        {path: '/ocr', icon: <Scan size={20}/>, label: 'Urdu OCR'},
        {path: '/paraphrasing', icon: <Type size={20}/>, label: 'Urdu Paraphrasing'},
        {path: '/update-aalim', icon: <Bot size={20}/>, label: 'Update Digital Aalim'},
        {path: '/view-chats', icon: <MessageSquare size={20}/>, label: 'View Chats'},
        {path: '/view-qalam-chats', icon: <MessageCircle size={20}/>, label: 'View Qalam Chats'},
        {path: '/view-users', icon: <Users size={20}/>, label: 'View Users'},
        {path: '/bulk-emails', icon: <Mail size={20}/>, label: 'Bulk Emails'},
        {path: '/view-maktab', icon: <Bot size={20}/>, label: 'View Maktab Chatbots'},
        {path: '/requested-books', icon: <Book size={20}/>, label: 'Requested Books'},
        {path: '/research-request', icon: <FileSearch size={20}/>, label: 'Research Requests'},
        {path: '/donations', icon: <HandCoins size={20}/>, label: 'Donations'},
        {path: '/announcements', icon: <Megaphone size={20}/>, label: 'Announcements'},
        {path: '/database-backups', icon: <Database size={20}/>, label: 'Database Backups'},
        {path: '/usage-control', icon: <ShieldCheck size={20}/>, label: 'Usage Control'},
        {path: '/feedbacks', icon: <MessageCircle size={20}/>, label: 'View Feedbacks'},
        {path: '/subscription', icon: <CreditCard size={20}/>, label: 'Subscription'},
        {path: '/settings', icon: <Settings size={20}/>, label: 'Settings'},
    ];

    return (
        <aside
            className="bg-gradient-to-b from-blue-800 to-blue-900 text-white w-64 h-screen flex flex-col shadow-lg overflow-hidden">
            <div className="p-4 border-b border-blue-700">
                <h1 className="text-xl font-bold">Digital Aalim</h1>
                <p className="text-xs text-blue-200">Admin Panel</p>
            </div>

            {/* wrapper to clip the scrollbar track */}
            <div className="flex-1 overflow-hidden">
                {/* relative so chevron positions inside this area */}
                <nav
                    ref={scrollRef}
                    className="relative h-full overflow-y-auto py-4 pr-4 mr-[-12px]
                     [-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden"
                >
                    <ul className="space-y-1 px-2">
                        {navItems.map((item) => (
                            <li key={item.path}>
                                <Link
                                    to={item.path}
                                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                                        currentPath === item.path
                                            ? 'bg-blue-700 text-white'
                                            : 'text-blue-100 hover:bg-blue-700/50'
                                    }`}
                                >
                                    {item.icon}
                                    <span className="text-sm font-medium">{item.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Scroll hint button */}
                    <button
                        type="button"
                        onClick={handleHintClick}
                        aria-label="Scroll down"
                        className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-10
                        text-blue-100 bg-blue-950/40 hover:bg-blue-900/60
                        ring-1 ring-blue-700/40 shadow-md
                        p-2 rounded-full transition-opacity duration-300
                        ${showScrollHint ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    >
                        <ChevronDown className="h-5 w-5 animate-bounce"/>
                    </button>
                </nav>
            </div>

            <div className="p-4 border-t border-blue-700">
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2 text-blue-100 hover:bg-red-500 rounded-lg transition-colors"
                >
                    <LogOut size={20}/>
                    <span className="text-sm font-medium">Logout</span>
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
